import React, {useCallback, useMemo} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DrawerFaceHeight} from 'components/customer/Product/Drawer/DrawerFaceHeight';
import {DrawerStyle} from 'components/customer/Product/Drawer/DrawerStyle';
import {DrawerType} from 'components/customer/Product/Drawer/DrawerType';
import {DrawerRunner} from 'components/customer/Product/Drawer/DrawerRunner';
import {RunnerPreview} from 'components/customer/Product/Drawer/RunnerPreview';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {DrawerThumbnail} from 'components/customer/Product/Drawer/DrawerThumbnail';
import {useProductContext} from 'contexts/ProductContext';
import {DrawerPosition} from 'components/customer/Product/Drawer/DrawerPosition';
import Icon from 'shared/Icon';
import {ProductContextType} from 'components/customer/Product/Specs';
import {CabinetDrawerType} from 'components/customer/Product/entity/Cabinet';
import {useFormikContext} from 'formik';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';

interface DrawerType {
    runnerIndex: number;
    showLine: boolean;
    drawerParams: DrawerParameters;
}

export const Drawer = ({
    runnerIndex,
    showLine = true,
    drawerParams,
}: DrawerType): JSX.Element => {
    const {isMiniBrowser, productDataStore} =
        useProductContext<ProductContextType>();
    const {setFieldValue, values} = useFormikContext<DrawerFields>();
    const isCustomInnerDrawer = useMemo(() => {
        const drawerType =
            productDataStore?.current?.cabinet?.attributes?.drawer_type;

        return (
            CabinetDrawerType[drawerType as keyof typeof CabinetDrawerType] ===
            CabinetDrawerType.inner_custom
        );
    }, []);

    const onDeleteDrawer = useCallback(() => {
        void setFieldValue('drawer_amount', Number(values.drawer_amount) - 1);
        void setFieldValue(
            'drawers',
            values.drawers?.filter((_, i) => i !== runnerIndex)
        );
    }, [values, setFieldValue, runnerIndex]);

    return (
        <>
            <Row className="drawer-runner">
                <Col
                    md={isMiniBrowser || drawerParams.isInnerDrawer ? 12 : 7}
                    xs={12}>
                    <div className="d-flex align-items-center gap-2">
                        <strong className="compound_title">
                            Drawer #{runnerIndex + 1}
                        </strong>
                        {isCustomInnerDrawer &&
                        Number(values.drawer_amount) > 1 ? (
                            <Icon
                                onClick={onDeleteDrawer}
                                style={{width: 20, height: 20, marginBottom: 2}}
                                iconName={'Options-Delete.svg'}
                            />
                        ) : null}
                    </div>

                    <DrawerType
                        runnerIndex={runnerIndex}
                        fieldName={drawerParams.drawerTypeFieldName}
                        isInnerDrawer={drawerParams.isInnerDrawer}
                    />
                    {drawerParams?.isInnerDrawer ? null : (
                        <>
                            <DrawerStyle
                                runnerIndex={runnerIndex}
                                fieldName={drawerParams.drawerStyleFieldName}
                            />
                            <DrawerFaceHeight
                                runnerIndex={runnerIndex}
                                fieldName={
                                    drawerParams.drawerFaceHeightFieldName
                                }
                                drawerStyleFieldName={
                                    drawerParams.drawerStyleFieldName
                                }
                            />
                        </>
                    )}
                    <DrawerRunner
                        isInnerDrawer={drawerParams.isInnerDrawer}
                        runnerIndex={runnerIndex}
                        fieldName={drawerParams.drawerRunnerFieldName}
                        drawerTypeFieldName={drawerParams.drawerTypeFieldName}
                        drawerFaceHeightFieldName={
                            drawerParams.drawerFaceHeightFieldName ||
                            'drawer_face_height'
                        }
                        fingerPullFieldName={drawerParams.fingerPullFieldName}
                        drawerDepth={drawerParams.maxRunnerDepth}
                    />

                    {drawerParams?.isInnerDrawer ? (
                        <DrawerPosition index={runnerIndex} />
                    ) : null}
                </Col>

                {drawerParams?.isInnerDrawer ? null : (
                    <>
                        <Col md={isMiniBrowser ? 6 : 2} xs={6}>
                            <RunnerPreview
                                runnerIndex={runnerIndex}
                                totalDrawerHeight={
                                    drawerParams.totalDrawerHeight
                                }
                                drawerGapFieldName={
                                    drawerParams.drawerGapFieldName
                                }
                                drawerTopMarginFieldName={
                                    drawerParams.drawerTopMarginFieldName
                                }
                                drawerBottomMarginFieldName={
                                    drawerParams.drawerBottomMarginFieldName
                                }
                                drawerFaceHeightFieldName={
                                    drawerParams.drawerFaceHeightFieldName
                                }
                            />
                        </Col>
                        <Col md={isMiniBrowser ? 6 : 3} xs={6}>
                            <DrawerThumbnail
                                runnerIndex={runnerIndex}
                                drawerTypeFieldName={
                                    drawerParams.drawerTypeFieldName
                                }
                                drawerFaceHeightFieldName={
                                    drawerParams.drawerFaceHeightFieldName
                                }
                                fingerPullFieldName={
                                    drawerParams.fingerPullFieldName
                                }
                                drawerDepth={drawerParams.maxRunnerDepth}
                            />
                        </Col>
                    </>
                )}
            </Row>
            {showLine ? <hr /> : null}
        </>
    );
};
